import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTablePreferences } from "./utils/useTablePreferences";
import DropdownColumnFilter from "./DropDownColumns";
import "../styles/ListPage.css";
import "../styles/BatchList.css";

const BatchList = () => {
  const navigate = useNavigate();

  const [batches, setBatches] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const { rowsPerPage, setRowsPerPage, sortConfig, setSortConfig } = useTablePreferences("table_prefs_batches");
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({ title: "", duration: "", trainer: "", time: "", days: "", status: "", activeStatus: "active" });
  const [loading, setLoading] = useState(false);
  const [importFailModal, setImportFailModal] = useState(false);
  const [importFailCount, setImportFailCount] = useState(0);
  const [importSuccess, setImportSuccess] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;
  const filterChangedRef = useRef(false);
  const fileInputRef = useRef(null);

  const fetchBatches = useCallback(async () => {
    setLoading(true);
    const queryParams = new URLSearchParams();
    queryParams.append("page", page + 1);
    queryParams.append("pageSize", rowsPerPage);
    if (searchTerm.trim()) queryParams.append("search", searchTerm.trim());

    const filterKeyMap = { title: "batchTitle", duration: "batchDuration", trainer: "trainer", time: "batchTime", days: "batchDays", status: "batchStatus", activeStatus: "activeStatus" };
    Object.entries(filters).forEach(([key, value]) => {
      if (value?.toString().trim()) queryParams.append(filterKeyMap[key] || key, value.toString().trim());
    });
    if (sortConfig.column && sortConfig.direction) {
      queryParams.append("sortBy", sortConfig.column);
      queryParams.append("order", sortConfig.direction);
    }

    try {
      const res = await fetch(`${API_URL}/api/batches?${queryParams.toString()}`);
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const response = await res.json();
      setBatches(response.data || []);
      setTotalCount(response.pagination?.totalItems || 0);
    } catch (error) {
      console.error("Error fetching batches:", error);
    }
    setLoading(false);
  }, [page, rowsPerPage, searchTerm, filters, sortConfig]);

  useEffect(() => { fetchBatches(); }, [fetchBatches]);

  useEffect(() => {
    if (filterChangedRef.current) { setPage(0); filterChangedRef.current = false; }
  }, [filters, searchTerm]);

  const totalPages = Math.ceil(totalCount / rowsPerPage);

  const onSort = (column) => {
    setPage(0);
    setSortConfig((prev) => {
      if (prev.column === column) {
        if (prev.direction === "asc") return { column, direction: "desc" };
        if (prev.direction === "desc") return { column: null, direction: null };
      }
      return { column, direction: "asc" };
    });
  };

  const handleFilterChange = (key, value) => {
    if (filters[key] !== value) {
      filterChangedRef.current = true;
      setFilters((prev) => ({ ...prev, [key]: value }));
    }
  };

  const thClass = (col) => sortConfig.column === col
    ? (sortConfig.direction === "asc" ? "lp-sort-asc" : "lp-sort-desc")
    : "";

  const handleRowClick = (id) => navigate(`/batches/${id}`);
  const handleAddBatch = () => navigate("/batches/add");

  const handleExportCSV = async () => {
    const queryParams = new URLSearchParams();
    const filterKeyMap = { title: "batchTitle", trainer: "trainerName", time: "batchTime", days: "batchDays", notes: "notesCount" };
    if (searchTerm.trim()) queryParams.append("search", searchTerm.trim());
    Object.entries(filters).forEach(([key, value]) => {
      const v = (value ?? "").toString().trim();
      if (v) queryParams.append(filterKeyMap[key] || key, v);
    });
    try {
      const response = await fetch(`${API_URL}/api/batches-export?${queryParams.toString()}`, { headers: { Accept: "text/csv" } });
      if (!response.ok) throw new Error("Failed to export");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      link.href = url; link.download = `batches-${timestamp}.csv`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) { console.error("CSV download error:", error); }
  };

  const handleCSVImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const EXPECTED_HEADERS = ["batchTitle","batchDuration","batchDescription","trainerName","batchTime","batchDays","status","noteTitle","noteDescription","noteLinkOfAttachment","noteDateTime","noteStatus"];
    const reader = new FileReader();
    reader.onload = async (event) => {
      const lines = event.target.result.trim().split("\n");
      const headers = lines[0].split(",").map((h) => h.trim());
      const isHeaderValid = headers.length === EXPECTED_HEADERS.length && headers.every((h, i) => h.toLowerCase() === EXPECTED_HEADERS[i].toLowerCase());
      if (!isHeaderValid) { setImportFailCount(lines.length - 1); setImportFailModal(true); return; }
      const formData = new FormData();
      formData.append("file", file);
      try {
        const res = await fetch("/api/batches-import", { method: "POST", body: formData });
        const response = await res.json();
        if (response.success) { fetchBatches(); setImportSuccess(true); setTimeout(() => setImportSuccess(false), 3000); }
        else { setImportFailCount(lines.length - 1); setImportFailModal(true); }
      } catch (err) { console.error("Import Error:", err); setImportFailCount(lines.length - 1); setImportFailModal(true); }
      finally { e.target.value = null; }
    };
    reader.readAsText(file);
  };

  const filterOptions = (key) => Array.from(new Set(batches.map((b) => {
    if (key === "days") return Array.isArray(b.batchDays) ? b.batchDays.join(", ") : b.batchDays;
    if (key === "time") return b.batchTime;
    if (key === "trainer") return b.trainer;
    if (key === "title") return b.batchTitle;
    if (key === "duration") return b.batchDuration;
    if (key === "status") return b.batchStatus;
    return "";
  }).filter(Boolean))).map((v) => ({ label: v, value: v }));

  return (
    <div className="lp-page">
      {importSuccess && <div className="lp-toast">Batch import successful</div>}

      {importFailModal && (
        <div className="lp-overlay">
          <div className="lp-modal">
            <h3>Import Failed</h3>
            <p>{importFailCount} row(s) could not be imported. Please download the sample CSV and try again.</p>
            <button className="lp-modal-btn" onClick={() => setImportFailModal(false)}>Close</button>
          </div>
        </div>
      )}

      {showImportModal && (
        <div className="lp-overlay" onClick={() => setShowImportModal(false)}>
          <div className="lp-drop-modal" onClick={(e) => e.stopPropagation()}>
            <div
              className={`lp-drop-zone${dragOver ? " lp-drag-active" : ""}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault(); setDragOver(false);
                const file = e.dataTransfer.files?.[0];
                if (file) { handleCSVImport({ target: { files: [file] } }); setShowImportModal(false); }
              }}
            >
              <h3>Upload CSV File</h3>
              <p>Drag a file here, or click Browse to select one.</p>
              <div className="lp-drop-actions">
                <button className="lp-btn-primary" onClick={() => fileInputRef.current?.click()}>Browse</button>
                <button className="lp-btn-secondary" onClick={() => {
                  const link = document.createElement("a");
                  link.href = "/sample-batch.csv"; link.download = "sample-batch.csv";
                  document.body.appendChild(link); link.click(); link.remove();
                }}>Download Sample</button>
              </div>
              <input type="file" accept=".csv" ref={fileInputRef} style={{ display: "none" }} onChange={(e) => { handleCSVImport(e); setShowImportModal(false); }} />
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="lp-header">
        <div className="lp-header-left">
          <h2 className="lp-title">All Batches</h2>
          <span className="lp-count">{totalCount} records</span>
        </div>
        <div className="lp-header-actions">
          <button className="lp-btn-primary" onClick={handleAddBatch}>+ Add Batch</button>
          <button className="lp-btn-secondary" onClick={handleExportCSV}>Export CSV</button>
          <button className="lp-btn-secondary" onClick={() => setShowImportModal(true)}>Import CSV</button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="lp-toolbar">
        <div className="lp-search-wrap">
          <span className="lp-search-icon">&#128269;</span>
          <input
            type="text"
            placeholder="Search batches…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="lp-search"
          />
        </div>
        <div className="lp-filters">
          {/* Separate from the Status filter beside it: that one is the batch's
              lifecycle (Active/Inactive/Deleted/Completed), this is the on/off
              switch that decides whether the batch is offered anywhere. */}
          <div className="bl-status-filter">
            <label htmlFor="bl-activeStatus">Show</label>
            <select
              id="bl-activeStatus"
              value={filters.activeStatus}
              onChange={(e) => handleFilterChange("activeStatus", e.target.value)}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="all">All</option>
            </select>
          </div>
          {["title","duration","trainer","time","days","status"].map((key) => (
            <div key={key} style={{ minWidth: 150 }}>
              <DropdownColumnFilter
                label={key.charAt(0).toUpperCase() + key.slice(1)}
                value={filters[key]}
                options={filterOptions(key)}
                onFilterChange={(val) => handleFilterChange(key, val)}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="lp-table-card">
        {loading ? (
          <div className="lp-state"><div className="lp-spinner" /><p>Loading batches…</p></div>
        ) : batches.length === 0 ? (
          <div className="lp-state"><p>No batches found.</p></div>
        ) : (
          <table className="lp-table">
            <thead>
              <tr>
                <th className={thClass("batchTitle")} onClick={() => onSort("batchTitle")}>Batch Title</th>
                <th className={thClass("batchDuration")} onClick={() => onSort("batchDuration")}>Duration</th>
                <th className={thClass("trainer")} onClick={() => onSort("trainer")}>Trainer</th>
                <th className={thClass("batchTime")} onClick={() => onSort("batchTime")}>Time</th>
                <th className={thClass("batchDays")} onClick={() => onSort("batchDays")}>Days</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((batch) => (
                <tr key={batch._id} onClick={() => handleRowClick(batch._id)}>
                  <td>{batch.batchTitle}</td>
                  <td>{batch.batchDuration}</td>
                  <td>{batch.trainer || "—"}</td>
                  <td>{batch.batchTime}</td>
                  <td>{Array.isArray(batch.batchDays) ? batch.batchDays.join(", ") : batch.batchDays}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {!loading && batches.length > 0 && (
        <div className="lp-pagination">
          <button className="lp-page-btn" onClick={() => setPage((p) => Math.max(p - 1, 0))} disabled={page === 0}>← Previous</button>
          <span className="lp-page-info">Page {page + 1} of {totalPages}</span>
          <button className="lp-page-btn" onClick={() => setPage((p) => Math.min(p + 1, totalPages - 1))} disabled={page >= totalPages - 1}>Next →</button>
          <div className="lp-page-size">
            <label htmlFor="bl-pageSize">Rows:</label>
            <select id="bl-pageSize" value={rowsPerPage} onChange={(e) => { setPage(0); setRowsPerPage(Number(e.target.value)); }}>
              {[10,20,30,50,75,100].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchList;
